import router from 'next/router'
import { useState, VFC } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { LargeButton } from 'src/elements/Button'
import { errorRed, lightgray, safetyOrange, truewhite } from 'src/styles/colors'
import { fontWeightMedium } from 'src/styles/font'
import { flexCenter } from 'src/styles/mixins'
import { FORGOT_PASSWORD } from 'src/utils/routes'
import { AVALIABLE_SPECIAL_CHARS, validatePassword } from 'src/utils/validators'
import styled from 'styled-components'
import { FormInput } from '../../Profile/formComponents'

type ResetPasswordFormData = {
  password: string
  passwordConfirmation: string
}
export const ResetPasswordForm: VFC<{
  submit: (password: string) => Promise<void>
}> = ({ submit }) => {
  const [errorMessage, setErrorMessage] = useState('')
  const [shouldRetry, setShouldRetry] = useState(false)
  const methods = useForm<ResetPasswordFormData>()
  const {
    handleSubmit,
    formState: { isSubmitting },
  } = methods
  const onSubmit = async (data: ResetPasswordFormData) => {
    const result = validatePassword(data.password)
    if (result) {
      setErrorMessage(result.error)
      return
    }
    if (data.password !== data.passwordConfirmation) {
      setErrorMessage('入力されたパスワードが一致していません。')
      return
    }
    await submit(data.password).catch(() => {
      setShouldRetry(true)
      setErrorMessage(
        'エラーが発生しました。既にパスワード再設定が行われているか、期限が超過している可能性があります。\nパスワード再設定を行う場合は、以下のボタンからパスワード再設定メールを再送してやり直してください。',
      )
      return Promise.reject()
    })
  }

  return (
    <Content>
      <h1>パスワードを再設定してください</h1>
      <p>
        {`パスワードはアルファベット大文字・小文字及び数字をそれぞれ1文字以上含めた、\n8文字以上98文字以下の文字列を設定してください。\n
        パスワードには上記の文字種に加えて以下の特殊文字を利用できます。\n
        ${AVALIABLE_SPECIAL_CHARS}`}
      </p>
      <FormProvider {...methods}>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <FormInput
            id="password"
            label="新しいパスワード"
            type="password"
            autoComplete="new-password"
            optional
          />
          <FormInput
            id="passwordConfirmation"
            label="新しいパスワード（確認）"
            type="password"
            autoComplete="new-password"
            optional
          />
          <ErrorMessage>{errorMessage}</ErrorMessage>
          {!shouldRetry ? (
            <SubmitButton disabled={isSubmitting}>
              再設定してログイン
            </SubmitButton>
          ) : (
            <SubmitButton
              type="button"
              onClick={() => router.push(FORGOT_PASSWORD)}
            >
              再送する
            </SubmitButton>
          )}
        </Form>
      </FormProvider>
    </Content>
  )
}
const ErrorMessage = styled.p`
  min-height: 1.5em;
  text-align: center;
  color: ${errorRed};
  line-height: 1.4;
`

const Form = styled.form`
  width: 100%;
`

const SubmitButton = styled(LargeButton)`
  background: ${safetyOrange};
  color: ${truewhite};
  min-width: 225px;
`

const Content = styled.div`
  margin: 48px auto;
  padding: 64px 48px;
  max-width: 640px;
  ${flexCenter};
  flex-direction: column;
  box-shadow: 0 0 20px rgb(0 0 0 / 30%);

  h1 {
    width: 100%;
    text-align: center;
    font-size: 26px;
    font-weight: ${fontWeightMedium};
    padding-bottom: 24px;
    margin-bottom: 24px;
    border-bottom: 1px solid ${lightgray}40;
  }
  p {
    text-align: center;
    white-space: pre-wrap;
  }
  ${ErrorMessage} {
    margin-top: 24px;
  }
  ${SubmitButton} {
    display: block;
    margin: 64px auto 0;
  }
`
